import type { DailyEventObject, DailyParticipant } from '@daily-co/daily-js';
import { useDailyEvent, useParticipantIds } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { useExistingSessionStarted } from '@web-app/hooks/queries/session-notes/useExistingSessionStarted';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { isDialOutParticipant } from '@web-app/utils/daily-client/dial-out-participant';
import { useCallback, useEffect, useState } from 'react';

/** Exclude a ringing dial-out leg until Daily confirms that it was answered. */
export const useConnectedParticipantIds = (roomId: string): string[] => {
  const [answeredDialOutIds, setAnsweredDialOutIds] = useState<Set<string>>(
    () => new Set(),
  );
  const {
    data: sessionStarted,
    isFetching,
    isError,
  } = useExistingSessionStarted(roomId);

  const filterConnected = useCallback(
    (participant: DailyParticipant) =>
      !isDialOutParticipant(participant) ||
      answeredDialOutIds.has(participant.session_id),
    [answeredDialOutIds],
  );

  const allParticipantIds = useParticipantIds();
  const connectedParticipantIds = useParticipantIds({
    filter: filterConnected,
  });
  const dialOutParticipantIds = useParticipantIds({
    filter: isDialOutParticipant,
  });

  useDailyEvent(
    'dialout-answered',
    useCallback(
      (event: DailyEventObject<'dialout-answered'>) => {
        const sessionId = 'sessionId' in event ? event.sessionId : undefined;
        if (typeof sessionId !== 'string' || !sessionId) {
          datadogLogs.logger.warn(
            'Dial-out answered without sessionId',
            callLogContext('dial-out', {
              roomId,
              participantType: 'provider',
            }),
          );
          return;
        }

        setAnsweredDialOutIds((current) => {
          if (current.has(sessionId)) return current;
          const next = new Set(current);
          next.add(sessionId);
          return next;
        });
      },
      [roomId],
    ),
  );

  useEffect(
    function logExistingSessionLoadError() {
      if (!isError || dialOutParticipantIds.length === 0) return;
      datadogLogs.logger.warn(
        'Could not load existing session; excluding unanswered dial-out lines',
        callLogContext('dial-out', {
          roomId,
          participantType: 'provider',
        }),
      );
    },
    [dialOutParticipantIds.length, isError, roomId],
  );

  // Treat a cached result as unverified while the mount-time request runs.
  return sessionStarted && !isFetching && !isError
    ? allParticipantIds
    : connectedParticipantIds;
};
